import type { Rule } from "../types";
import { makeFiring } from "../util";

const EVIDENCE = "seiler_2010_polarized_80_20";

/**
 * Last 28 days of endurance sessions: low-intensity (Z1-Z2) share < 70 %.
 * Seiler 2010: ~80% Z1-Z2 / ~20% Z4-Z5 outperforms threshold-heavy
 * distributions. Sessions with avg HR < 78% of LTHR are treated as low; we
 * fall back to RPE ≤ 5 when avg HR isn't available.
 *
 * Endurance sports only; strength/yoga/etc. are excluded from the denominator.
 */
const ENDURANCE_RX = /run|cycling|bike|swim|row|ski|trail|gravel|tt/;

export const polarizedRatioOff: Rule = {
  id: "polarized_ratio_off",
  domain: "health.training",
  topics: ["polarized_training", "z2", "intensity_distribution"],
  evidence_refs: [EVIDENCE],
  title: () => "Z2 share below 70%",
  evaluate(ctx) {
    const now = ctx.now.getTime();
    const day = 86_400_000;
    const endurance = ctx.sessions.filter(
      (s) =>
        ENDURANCE_RX.test(s.sport_id) &&
        now - new Date(s.end_ts).getTime() <= 28 * day,
    );
    if (endurance.length < 6) return null;

    const lowCount = endurance.filter((s) => (s.rpe ?? 5) <= 5).length;
    const lowShare = lowCount / endurance.length;
    if (lowShare >= 0.7) return null;

    return makeFiring({
      rule_id: "polarized_ratio_off",
      now: ctx.now,
      tz: ctx.tz,
      severity: "warn",
      message: `Last 28 days: only ${(lowShare * 100).toFixed(0)}% of endurance sessions were low-intensity (RPE ≤ 5). Polarized targets ~80%.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "z2_share_pct",
          value: Number((lowShare * 100).toFixed(0)),
          contribution: "add Z2 volume",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.training",
    });
  },
};
